import { useEffect, useState, useCallback } from "react";
import { Accessibility, Type, Contrast, Volume2, VolumeX, X, Pause, Play } from "lucide-react";
import { useTranslation } from "react-i18next";

const TEXT_SIZE_KEY = "vidalv-text-size";   // "normal" | "larger" | "largest"
const CONTRAST_KEY = "vidalv-contrast";     // "normal" | "high"
const IMAGE_MODE_KEY = "vidalv-image-mode"; // Build 94b: "normal" | "simplified"

const SIZE_LABELS = { normal: "A", larger: "A+", largest: "A++" };
const SIZE_MAP = { normal: "1", larger: "1.18", largest: "1.35" };

function applyTextSize(size) {
  document.documentElement.style.setProperty("--vidalv-text-scale", SIZE_MAP[size] || "1");
  document.documentElement.dataset.textSize = size;
}
function applyContrast(mode) {
  if (mode === "high") document.documentElement.classList.add("vidalv-high-contrast");
  else document.documentElement.classList.remove("vidalv-high-contrast");
  document.documentElement.dataset.contrast = mode;
}
// Build 94b — "Förenkla bilder"-läge: döljer dekorativa galleri-bilder, dämpar resten.
function applyImageMode(mode) {
  if (mode === "simplified") document.documentElement.classList.add("vidalv-images-simplified");
  else document.documentElement.classList.remove("vidalv-images-simplified");
  document.documentElement.dataset.imageMode = mode;
}

// Map i18n-code → BCP47 voice tag (best effort)
const VOICE_LANG = {
  sv: "sv-SE", se: "sv-SE",   // sami – fallback till svenska
  en: "en-GB", de: "de-DE", nl: "nl-NL",
  pl: "pl-PL", uk: "uk-UA", ru: "ru-RU", th: "th-TH",
};

function pickVoice(lang) {
  const synth = window.speechSynthesis;
  if (!synth) return null;
  const voices = synth.getVoices() || [];
  const want = VOICE_LANG[lang] || "sv-SE";
  const exact = voices.find(v => v.lang === want);
  if (exact) return exact;
  const partial = voices.find(v => v.lang.startsWith(want.slice(0, 2)));
  return partial || voices[0] || null;
}

/**
 * Sammanställer läsbar text från huvudinnehållet.
 * Utelämnar header, footer, nav, knappar. Begränsar till synlig text.
 */
function collectReadableText() {
  const root = document.querySelector("main") || document.body;
  const clone = root.cloneNode(true);
  // Strip elements we don't want read aloud
  clone.querySelectorAll("nav, header, footer, script, style, button, input, select, [aria-hidden='true'], [data-testid='vidalv-a11y-drawer'], [data-testid='vidalv-a11y-btn']")
    .forEach(el => el.remove());
  let text = (clone.innerText || "").replace(/\s+/g, " ").trim();
  // Limit to ~3000 chars to avoid very long sessions
  if (text.length > 3000) text = text.slice(0, 3000) + "…";
  return text;
}

export default function AccessibilityToolbar() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState(() => localStorage.getItem(TEXT_SIZE_KEY) || "normal");
  const [contrast, setContrast] = useState(() => localStorage.getItem(CONTRAST_KEY) || "normal");
  const [imageMode, setImageMode] = useState(() => localStorage.getItem(IMAGE_MODE_KEY) || "normal");
  const [ttsState, setTtsState] = useState("idle"); // idle | speaking | paused

  useEffect(() => { applyTextSize(size); localStorage.setItem(TEXT_SIZE_KEY, size); }, [size]);
  useEffect(() => { applyContrast(contrast); localStorage.setItem(CONTRAST_KEY, contrast); }, [contrast]);
  useEffect(() => { applyImageMode(imageMode); localStorage.setItem(IMAGE_MODE_KEY, imageMode); }, [imageMode]);

  // Stop TTS when drawer closes or route changes (detect via URL change)
  useEffect(() => {
    const handler = () => {
      window.speechSynthesis?.cancel();
      setTtsState("idle");
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const startRead = useCallback(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const text = collectReadableText();
    if (!text) return;
    // Voices may load async – force update
    const speak = () => {
      const utterance = new SpeechSynthesisUtterance(text);
      const lang = (i18n.language || "sv").split("-")[0];
      const voice = pickVoice(lang);
      if (voice) utterance.voice = voice;
      utterance.lang = voice?.lang || VOICE_LANG[lang] || "sv-SE";
      utterance.rate = 0.95;
      utterance.onend = () => setTtsState("idle");
      utterance.onerror = () => setTtsState("idle");
      synth.speak(utterance);
      setTtsState("speaking");
    };
    if (synth.getVoices().length === 0) {
      synth.onvoiceschanged = () => { speak(); synth.onvoiceschanged = null; };
    } else {
      speak();
    }
  }, [i18n.language]);

  const togglePause = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (ttsState === "speaking") { synth.pause(); setTtsState("paused"); }
    else if (ttsState === "paused") { synth.resume(); setTtsState("speaking"); }
  };

  const stopRead = () => {
    window.speechSynthesis?.cancel();
    setTtsState("idle");
  };

  return (
    <>
      <button
        data-testid="vidalv-a11y-btn"
        aria-label={t("a11y.open", { defaultValue: "Öppna tillgänglighetsverktyg" })}
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-3 right-3 sm:bottom-5 sm:left-5 sm:right-auto z-[1000] bg-ink text-parchment w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg hover:bg-ink/90 flex items-center justify-center transition-transform hover:scale-105 opacity-80 sm:opacity-100 hover:opacity-100"
      >
        <Accessibility size={18} className="sm:w-[22px] sm:h-[22px]" />
      </button>

      {open && (
        <div
          data-testid="vidalv-a11y-drawer"
          className="fixed bottom-16 right-3 sm:bottom-20 sm:left-5 sm:right-auto z-[1000] bg-parchment border border-warm-border rounded-sm shadow-2xl p-4 w-[280px] sm:w-[300px]"
          role="dialog"
          aria-label={t("a11y.panel_title", { defaultValue: "Tillgänglighetsverktyg" })}
        >
          <div className="flex items-center justify-between mb-4">
            <p className="font-fraktur-smooth text-falu text-lg tracking-wide">
              {t("a11y.panel_title", { defaultValue: "Tillgänglighet" })}
            </p>
            <button data-testid="a11y-close" onClick={() => setOpen(false)} aria-label="Stäng" className="text-bark/60 hover:text-ink">
              <X size={18} />
            </button>
          </div>

          {/* Textstorlek */}
          <div className="mb-4">
            <p className="text-[11px] font-body font-semibold uppercase tracking-[0.18em] text-bark/60 mb-2 flex items-center gap-1.5">
              <Type size={12} /> {t("a11y.text_size", { defaultValue: "Textstorlek" })}
            </p>
            <div className="flex gap-1" role="group">
              {["normal", "larger", "largest"].map(s => (
                <button
                  key={s}
                  data-testid={`a11y-size-${s}`}
                  onClick={() => setSize(s)}
                  className={`flex-1 text-center py-2 rounded-sm border transition-colors ${
                    size === s
                      ? "bg-ink text-parchment border-ink"
                      : "bg-parchment text-ink border-warm-border hover:border-bark/40"
                  }`}
                  style={{ fontSize: s === "normal" ? "14px" : s === "larger" ? "17px" : "20px" }}
                  aria-pressed={size === s}
                >
                  {SIZE_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Kontrast */}
          <div className="mb-4">
            <p className="text-[11px] font-body font-semibold uppercase tracking-[0.18em] text-bark/60 mb-2 flex items-center gap-1.5">
              <Contrast size={12} /> {t("a11y.contrast", { defaultValue: "Kontrast" })}
            </p>
            <div className="flex gap-1" role="group">
              <button
                data-testid="a11y-contrast-normal"
                onClick={() => setContrast("normal")}
                className={`flex-1 text-xs py-2 rounded-sm border transition-colors ${
                  contrast === "normal" ? "bg-ink text-parchment border-ink" : "bg-parchment text-ink border-warm-border"
                }`}
                aria-pressed={contrast === "normal"}
              >
                {t("a11y.contrast_normal", { defaultValue: "Normal" })}
              </button>
              <button
                data-testid="a11y-contrast-high"
                onClick={() => setContrast("high")}
                className={`flex-1 text-xs py-2 rounded-sm border transition-colors ${
                  contrast === "high" ? "bg-ink text-parchment border-ink" : "bg-parchment text-ink border-warm-border"
                }`}
                aria-pressed={contrast === "high"}
              >
                {t("a11y.contrast_high", { defaultValue: "Hög" })}
              </button>
            </div>
          </div>

          {/* Build 94b — Bilder/visning */}
          <div className="mb-4">
            <p className="text-[11px] font-body font-semibold uppercase tracking-[0.18em] text-bark/60 mb-2 flex items-center gap-1.5">
              {t("a11y.images", { defaultValue: "Bilder" })}
            </p>
            <div className="flex gap-1" role="group">
              <button
                data-testid="a11y-images-normal"
                onClick={() => setImageMode("normal")}
                className={`flex-1 text-xs py-2 rounded-sm border transition-colors ${
                  imageMode === "normal" ? "bg-ink text-parchment border-ink" : "bg-parchment text-ink border-warm-border"
                }`}
                aria-pressed={imageMode === "normal"}
              >
                {t("a11y.images_normal", { defaultValue: "Normal" })}
              </button>
              <button
                data-testid="a11y-images-simplified"
                onClick={() => setImageMode("simplified")}
                className={`flex-1 text-xs py-2 rounded-sm border transition-colors ${
                  imageMode === "simplified" ? "bg-ink text-parchment border-ink" : "bg-parchment text-ink border-warm-border"
                }`}
                aria-pressed={imageMode === "simplified"}
              >
                {t("a11y.images_simplified", { defaultValue: "Förenkla" })}
              </button>
            </div>
            <p className="text-[10px] text-bark/45 italic mt-1.5 leading-relaxed">
              {t("a11y.images_hint", { defaultValue: "'Förenkla' döljer dekorativa bildspel och dämpar resten." })}
            </p>
          </div>

          {/* Läs upp */}
          <div>
            <p className="text-[11px] font-body font-semibold uppercase tracking-[0.18em] text-bark/60 mb-2 flex items-center gap-1.5">
              <Volume2 size={12} /> {t("a11y.read_aloud", { defaultValue: "Läs upp sidan" })}
            </p>
            {ttsState === "idle" ? (
              <button
                data-testid="a11y-read-start"
                onClick={startRead}
                className="w-full text-sm font-body font-semibold bg-pine text-parchment py-2.5 rounded-sm hover:bg-pine-hover transition-colors flex items-center justify-center gap-2"
              >
                <Volume2 size={14} /> {t("a11y.read_start", { defaultValue: "Starta uppläsning" })}
              </button>
            ) : (
              <div className="flex gap-1">
                <button
                  data-testid="a11y-read-toggle"
                  onClick={togglePause}
                  className="flex-1 text-sm font-body font-semibold bg-pine text-parchment py-2.5 rounded-sm hover:bg-pine-hover transition-colors flex items-center justify-center gap-2"
                >
                  {ttsState === "speaking" ? <><Pause size={13} /> Pausa</> : <><Play size={13} /> Fortsätt</>}
                </button>
                <button
                  data-testid="a11y-read-stop"
                  onClick={stopRead}
                  className="text-sm font-body font-semibold bg-parchment text-falu border border-falu/30 py-2.5 px-3 rounded-sm hover:bg-falu/5 transition-colors flex items-center justify-center"
                  aria-label="Stopp"
                >
                  <VolumeX size={14} />
                </button>
              </div>
            )}
            <p className="text-[10px] text-bark/45 italic mt-2 leading-relaxed">
              {t("a11y.read_hint", { defaultValue: "Läser huvudinnehållet på sidan. Funkar på de flesta moderna webbläsare." })}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
